import { createFileRoute, Link } from '@tanstack/react-router'
import { css } from '../../../../styled-system/css'
import Reload from '~icons/material-symbols/refresh.svg'
import Add from '~icons/material-symbols/add.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import { button } from '../../../styles/buttonStyles'
import { dropdownMenuContent, dropdownMenuItem, dropdownMenuTrigger } from '../../../styles/dropdownMenuStyles'
import { input } from '../../../styles/inputStyles'
import { table, tableHead, tableHeadCell, tableRow, tableCell, tableWrapper } from '../../../styles/tableStyles'
import { pageTitle, titleWrapper } from '../../../styles/sectionStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { useEffect, useState } from 'react'
import { atom, useAtom, useAtomValue } from 'jotai'
import { useGetMyCompanies } from '../../../libs/admin/hooks'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../components/table-pagination-toolbar'
import { Popover } from 'radix-ui'
import ArrowDropDown from '~icons/material-symbols/arrow-drop-down.svg'
import { flattenCompanyStores } from '../-company-store-csv'
import { CompanyCsvDownloadButton, StoreCsvDownloadButton } from '../-store-csv-download-button'

export const Route = createFileRoute('/admin/companies/')({
  component: RouteComponent,
})

const incrementalSearchTextAtom = atom<string>('');

const Toolbar = () => {
  const [incrementalSearchText, setIncrementalSearchText] = useAtom(incrementalSearchTextAtom);
  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      alignItems: 'end',
    })}>
      {/* 事業者名入力欄 */}
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        width: '240px',
      })}>
        <div className={css({
          fontSize: '12px',
        })}>事業者名</div>
        <input
          type="text"
          placeholder='例）○○コーポレーション'
          className={input()}
          value={incrementalSearchText}
          onChange={(e) => {
            setIncrementalSearchText(e.target.value);
          }}
        />
      </div>
      <button
        type="reset"
        className={button()}
        onClick={() => {
          setIncrementalSearchText('');
        }}
      >
        <Reload />
        リセット
      </button>
      <div className={css({
        flexGrow: 1,
      })} />
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={dropdownMenuTrigger()}>
            一括登録
            <ArrowDropDown />
          </button>
        </Popover.Trigger>
        <Popover.Content className={dropdownMenuContent()} sideOffset={8} align="end">
          <Link className={dropdownMenuItem()} to='/admin/companies/bulkCreateCompanies'>
            事業者・店舗一括登録
          </Link>
          <Link className={dropdownMenuItem()} to='/admin/companies/bulkCreateStaffAccounts'>
            事業者アカウント一括登録
          </Link>
          <Link className={dropdownMenuItem()} to='/admin/companies/bulkCreateStoreAccounts'>
            店舗アカウント一括登録
          </Link>
          <Link className={dropdownMenuItem()} to='/admin/companies/bulkCreateQrcodes'>
            店舗QR一括作成
          </Link>
        </Popover.Content>
      </Popover.Root>
      <Link to='/admin/companies/newCompany'>
        <button type="button" className={button({ color: 'primary' })}>
          <Add />
          事業者登録
        </button>
      </Link>
    </div>
  )
}

const Table = () => {
  const { data, isLoading } = useGetMyCompanies();
  
  // 事業者情報
  const myCompanies = data ?? [];
  const incrementalSearchText = useAtomValue(incrementalSearchTextAtom);
  const companyWithStores = myCompanies.filter((company) => {
    const _company = company.company;
    if (!_company) {
      return false;
    }
    return _company.name.includes(incrementalSearchText);
  });

  // ページネーションのための状態管理
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(companyWithStores.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages - 1);
  const startRow = currentPage * rowsPerPage;
  const endRow = startRow + rowsPerPage;
  const paginatedCompanies = companyWithStores.slice(startRow, endRow);
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage >= totalPages) {
      return;
    }
    setPage(newPage);
  }

  useEffect(() => {
    setPage(0);
  }, [incrementalSearchText]);
  const hasDownloadableRows = companyWithStores.length > 0;

  return (
    <>
      <div className={tableWrapper()}>
        {/* ローディング */}
        {isLoading && (<LoadingOverlay />)}
        <TablePaginationToolbar
          page={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        <table className={table()}>
          <thead className={tableHead()}>
            <tr>
              <th className={tableHeadCell()}>事業者名</th>
              <th className={tableHeadCell()}>登録代表者名</th>
              <th className={tableHeadCell()}>登録住所</th>
              <th className={tableHeadCell()}>電話番号</th>
              <th className={tableHeadCell()} />
            </tr>
          </thead>
          {/* 事業者情報 */}
          {paginatedCompanies.map((company) => {
            const _company = company.company;
            if (!_company) {
              return null;
            }
            return (
              <tbody key={_company.id}>
                <tr className={tableRow()}>
                  <td className={tableCell()}>{_company.name}</td>
                  <td className={tableCell()}>{_company.ownerName}</td>
                  <td className={tableCell()}>{_company.address}</td>
                  <td className={tableCell()}>{_company.contact}</td>
                  <td className={tableCell()}>
                    <div className={css({
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    })}>
                      <Link
                        to="/admin/companies/$companyId"
                        params={{ companyId: _company.id }}
                      >
                        <button type='button' className={button()}>
                          詳細<ArrowForward />
                        </button>
                      </Link>
                    </div>
                  </td>
                </tr>
              </tbody>
            )
          })}
          {/* 事業者情報がない場合 */}
          {companyWithStores.length === 0 && (
            <tbody>
              <tr className={tableRow()}>
                <td colSpan={5} className={css({
                  textAlign: 'center',
                  padding: '16px',
                })}>
                  事業者情報がありません
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      <div className={css({
        display: 'flex',
        justifyContent: 'flex-start',
      })}>
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type='button'
              className={dropdownMenuTrigger()}
              disabled={isLoading || !hasDownloadableRows}
            >
              一覧CSVをダウンロード
              <ArrowDropDown />
            </button>
          </Popover.Trigger>
          <Popover.Content className={dropdownMenuContent()} sideOffset={8} align="start">
            <CompanyCsvDownloadButton
              className={dropdownMenuItem()}
              companies={companyWithStores}
            >
              事業者一覧CSVをダウンロード
            </CompanyCsvDownloadButton>
            <StoreCsvDownloadButton
              className={dropdownMenuItem()}
              stores={flattenCompanyStores(companyWithStores)}
            >
              店舗一覧CSVをダウンロード
            </StoreCsvDownloadButton>
          </Popover.Content>
        </Popover.Root>
      </div>
    </>
  )
}

function RouteComponent() {
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>事業者</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <Toolbar />
        <Table />
      </div>
    </div>
  )
}
